import { create } from 'zustand';
import { STARTING_CURRENCIES, type TradingPlazaConfig } from '@/config/balance.config';
import type { BankConfig } from '@/config/bank.types';
import { DEFAULT_STARTER_SKIN_ID } from '@/config/avatars.config';
import { readMusicEnabledPreference, readMusicTrackPreference } from '@/shared/audioSettings';
import type { ActiveEffects, PotionsBag } from '@/config/potions.config';
import type { MusicTrackId } from '@/config/music.config';
import type { AvatarSkinId } from '@/domain/types/avatar';
import type { EggInstance } from '@/domain/types/egg';
import type { PetInstance, PetDef } from '@/domain/types/pet';
import type { Variant, ID } from '@/domain/types/common';
import type { TradeOfferView } from '@/net/plazaRoom';
import type { BankStickerState } from '@/net/api';
import type { Coins } from '@/domain/economy/coins';
import { mergeSaleNotices, readSeenSaleIds, writeSeenSaleIds, type StallSale } from '@/ui/stallSales';

/** Pusta oferta handlu — default + reset (5.C.2). */
const EMPTY_TRADE_OFFER: TradeOfferView = { pets: [], crystals: 0, eggs: [], locked: false, confirmed: false };

/**
 * Toast odblokowanego achievementu (5.D.2d). `key` = unikalny identyfikator
 * instancji toastu (do animacji/usuwania), `id`/`label`/`crystalsReward` z API.
 */
export interface AchievementToastItem {
  key: number;
  id: string;
  label: string;
  crystalsReward: number;
}

/** Monotoniczny licznik kluczy toastów (unikalność niezależna od id achievementu). */
let achievementToastSeq = 0;

/**
 * Centralny store stanu UI (Zustand).
 *
 * REGUŁY:
 *  - Phaser NIE czyta z tego store'a.
 *  - React komponenty czytają STĄD przez selectory.
 *  - EventBus → Zustand bridge tłumaczy zmiany z Phaser'a.
 *  - Modale mutują przez `saveManager.update(recipe)` + sync setState'a.
 *
 * INITIAL STATE jest tylko placeholder'em. Faktyczna hydratacja dzieje się
 * w `main.tsx` po `await saveManager.load()`.
 */

/** Możliwe modale do otwarcia z HUD'a. `null` = brak otwartego modala. */
export type OpenModal =
  | null
  | 'eggs'
  | 'eggInventory'
  | 'pets'
  | 'merge'
  | 'upgrades'
  | 'map'
  | 'leaderboard'
  | 'account'
  | 'daily'
  | 'myStall'
  | 'tradeHistory'
  | 'browseStall'
  | 'traderMenu'
  | 'eggExchange'
  | 'npcSell'
  | 'potionShop'
  | 'potionBag'
  | 'bankHub'
  | 'bankAlbum'
  | 'posterGallery'
  | 'bankWork'
  | 'bankDeposit'
  | 'bankVault'
  | 'achievements'
  | 'eventAlbum'
  | 'eventDailyReward'
  | 'avatarCustomize'
  | 'settings';

/**
 * Stan UI handlu bezpośredniego 1-na-1 (Etap 5.C.1, polish 5.C.5). `null` = brak handlu.
 *  - `invite`    — ktoś zaprosił NAS (modal Tak/Nie),
 *  - `pending`   — MY zaprosiliśmy, czekamy na akceptację,
 *  - `active`    — handel trwa (okno wymiany),
 *  - `completed` — wymiana sfinalizowana (5.C.4), pokazujemy 🎉 ~1.5s zanim
 *                  zamkniemy modal (polish 5.C.5).
 * `peerName` = nick drugiej strony, `tradeId` z serwera realtime.
 */
export interface TradeUiState {
  phase: 'invite' | 'pending' | 'active' | 'completed';
  tradeId: string;
  peerName: string;
}

/**
 * Faza cinematic hatcha (sesja 29 — Step A).
 *
 *  - `null`        — brak hatcha aktywnego (normalne UI, gameplay działa)
 *  - `animating`   — Phaser HatchScene odgrywa cinematic. React ukrywa HUD/joystick/
 *                    modale (Wariant A z PHASER_ANIMATION_ANALYSIS) — czysty canvas
 *                    żeby gracz widział Phaser animation bez DOM overlay.
 *  - `showResult`  — Animacja skończona. React pokazuje finalny panel z nazwą peta,
 *                    rarity, variant, power i przycisk "Świetnie!" / close.
 *
 * Flow:
 *   null → animating → showResult → null
 *
 * Source of truth: React/Zustand. Phaser tylko sygnalizuje przez eventBus
 * (`hatch:complete` po końcu animacji) — React odpowiada przez setHatchPhase.
 */
export type HatchPhase = null | 'animating' | 'showResult';

/**
 * Wynik wyklucia jajka — przekazywany do `HatchAnimationModal`.
 * Trzymany w Zustand żeby modal mógł zostać re-renderowany niezależnie od
 * trigger'u (np. EggInventoryModal pod spodem zostaje otwarty).
 *
 * **`nonce`** (sesja 29A review LOW fix): unikalny identyfikator KAŻDEGO
 * hatcha, niezależnie czy ten sam pet+variant+egg już wystąpił. Bez tego,
 * `petId+variant+eggDefId` byłby duplikat przy multi-hatch lub powtórnym
 * wykluciu identycznego peta. EggInventoryModal generuje przez `Date.now()`
 * (krótki + unikalny w kontekście jednej sesji). HatchAnimationModal używa
 * jako `startedForResult` key — gwarantuje że KAŻDY nowy hatch wystartuje
 * cinematic, nawet identyczny do poprzedniego.
 */
export interface HatchResult {
  petDef: PetDef;
  variant: Variant;
  eggDefId: ID;
  isHuge: boolean;
  /** Unique-per-hatch — patrz komentarz powyżej. */
  nonce: number;
}

/**
 * Wynik bulk hatcha (5.D.3c). Trzymany w osobnym polu store'a żeby single i
 * bulk flow nie kolidowały. HatchAnimationModal renderuje grid panel dla bulk
 * w fazie `showResult`. `pets` jest w naturalnej kolejności hatcha (po FIFO
 * uidów), HatchBulkDirector sortuje wewnętrznie wg rarity do reveal phase.
 */
export interface HatchBulkResult {
  pets: Array<{
    petDef: PetDef;
    variant: Variant;
    isHuge: boolean;
  }>;
  eggDefId: ID;
  /** Liczba "nowych typów" w paczce — defIdów, których gracz nie miał wcześniej. */
  newSpeciesCount: number;
  /** Unique-per-hatch (Date.now()) — patrz `HatchResult.nonce`. */
  nonce: number;
}

export interface AuthUserSummary {
  id: string;
  username: string;
  /** Konto-gość (tryb gościa) — UI „claim" pokaże się tylko dla gościa. */
  is_guest: boolean;
  /** Rozwiązany display name (5.0.F): manualny / ładny nick gościa / username. */
  display_name: string;
}

export interface GameState {
  // ─── Auth (sesja 33) ─────────────────────────────────────────────────────
  /**
   * Czy gracz ma ważną sesję (httpOnly cookie z JWT) z backendem Capibara.
   * Po sesji guest core: zawsze `true` w punkcie renderu App — `resolveSession`
   * w `main.tsx` gwarantuje sesję (cookie / re-login gościa / nowe konto-gość)
   * PRZED React mount. Jeśli wszystkie 3 ścieżki padną → `showBootstrapError`
   * (DOM-only fallback, App się nigdy nie zamontuje).
   *
   * Setowane też przez `AccountModal` po sukcesie claim. Reset na logout
   * przed `location.reload()` → bootstrap zrobi auto-guest.
   *
   * Sesja 33: gate auth → avatar → starter pets. Save lokalny IDB zostaje
   * per-browser bez per-user namespace (migracja w 4.E).
   */
  isAuthenticated: boolean;

  /**
   * Aktywna scena Phaser (Etap 5.B.3). 'world' = biom (meadow/mountain),
   * 'plaza' = scena placu handlowego. Ustawiane przez scenę w create().
   * React używa do pokazania przycisku „Wróć" w plazie + ukrycia nieistotnego HUD.
   */
  bossIntroActive: boolean;
  activeScene: 'world' | 'plaza' | 'sanctum' | 'halloweenBoss';
  currentUser: AuthUserSummary | null;

  /**
   * Czy trwa widowiskowa animacja teleportu (`TeleportScene` w Phaserze) —
   * zmiana biomu, wejście na plac lub powrót z placu. Gdy `true`, App ukrywa
   * HUD/joystick/modale (jak przy hatch cinematic — Wariant A), żeby canvas
   * z portalem był czysty. Ustawiane przez `startTeleport()` (helper FX),
   * zerowane przez `TeleportScene` w SHUTDOWN (gwarantowany cleanup — brak
   * ryzyka „UI zniknęło na zawsze").
   */
  isTeleporting: boolean;

  /**
   * Czy stan gry został załadowany z serwera (sesja 34B). Bootstrap:
   *   auth → render React → waitForAuth → saveManager.load() (server) → hydrate
   *   → setSaveLoaded(true)
   *
   * App.tsx: między zalogowaniem a załadowaniem save pokazuje LoadingScreen
   * (bez tego mignąłby AvatarSelector z placeholder store state).
   */
  saveLoaded: boolean;

  /**
   * Czy Phaser (PreloadScene) skończył ładować grafiki gry. Bootstrap woła
   * `createGame()` PO onboardingu — PreloadScene ładuje wszystkie tekstury i
   * raportuje postęp tutaj. App.tsx trzyma splash z logo + pasek postępu na
   * wierzchu (zamiast pokazywać surowe zielone tło Phasera z osobnym paskiem),
   * aż `assetsLoaded === true`.
   *
   * `assetLoadProgress` ∈ [0, 1] — wypełnienie paska na splashu.
   */
  assetsLoaded: boolean;
  assetLoadProgress: number;

  /**
   * Config Trading Plaza pobrany przez `GET /config/trading-plaza` (Etap 5,
   * 5.0.0b). Single source of truth dla UI — komponenty czytają stąd
   * (a nie z `balance.config.ts`), żeby zmiana cen w configu serwera
   * propagowała się bez przebudowy frontu.
   *
   * `null` przed bootstrap fetch. Po `setTradingConfig` zawsze ustawione
   * (jeśli fetch padnie → showBootstrapError, App się nie zamontuje).
   *
   * `tradingConfigVersion` (np. 'v1') — używany do audit (forward-compat
   * A/B testing balansu).
   */
  tradingConfig: TradingPlazaConfig | null;
  tradingConfigVersion: string | null;

  /**
   * Config Banku pobrany przez `GET /config/bank` (BANK-0) — ten sam wzorzec
   * co `tradingConfig`: single source of truth dla UI banku (progi naklejek,
   * stawki pracy, oprocentowanie lokat). `null` przed bootstrap fetch; po
   * `setBankConfig` zawsze ustawione (fetch pada → showBootstrapError).
   */
  bankConfig: BankConfig | null;
  bankConfigVersion: string | null;

  /**
   * Album naklejek banku (BANK-A2): defId → stan naklejki/fotografii. Reflektor
   * `GET /bank/state` — hydratowany przy otwarciu albumu (fetch on-mount),
   * aktualizowany punktowo po paste (`mergeBankSticker` z odpowiedzi serwera).
   * `null` = jeszcze nie pobrany (modal pokazuje spinner). CELOWO poza
   * PlayerSave — stan banku żyje po stronie serwera (zero migracji save'ów).
   */
  bankAlbum: Record<string, BankStickerState> | null;
  /**
   * Licznik petów w skarbcu (BANK-D). SAMA LICZBA — zawartość NIGDY nie trafia
   * do store'a, bo skarbiec ma mieścić dziesiątki tysięcy petów i ładuje się
   * stronami dopiero po otwarciu okna (DESIGN_BANK_VAULT.md §2).
   */
  bankVaultCount: number;
  /** Sufit skarbca (V-5). 0 = jeszcze nieznany — kafel pokaże sam licznik. */
  bankVaultCap: number;
  /**
   * Lokalny stan gry jest NIEPEWNY — trzeba odzyskać autorytatywny save,
   * zanim wolno wykonać kolejną akcję zmieniającą ekwipunek.
   *
   * DLACZEGO W STORE, A NIE W KOMPONENCIE (Codex V-4 FIXES³ MED 1): dryf
   * dotyczy CAŁEGO plecaka, nie jednego ekranu. Gdy flaga żyła w
   * `BankVaultModal`, wystarczyło zamknąć okno i otworzyć je z huba, żeby
   * powstała nowa instancja z wyzerowaną blokadą — i ta sama operacja szła
   * drugi raz na niepewnym stanie.
   *
   * Zdejmuje ją WYŁĄCZNIE udany resync (`doResync` w `economyActions`), więc
   * każde poprawne odzyskanie save'a — skądkolwiek — odblokowuje grę.
   */
  economyResyncRequired: boolean;

  /**
   * Prace w kopalniach banku (BANK-B): reflektor `GET /bank/state`.jobs.
   * `null` = nie pobrane. Aktualizowane po assign/collect/recall z odpowiedzi
   * akcji. Jak `bankAlbum` — CELOWO poza PlayerSave.
   */
  bankJobs: Array<{ petUid: string; job: 'coins' | 'gems'; startedAt: string; lastCollectedAt: string }> | null;

  /**
   * Aktywne lokaty (BANK-C): reflektor `GET /bank/state`.deposits. `null` =
   * nie pobrane. Aktualizowane po open/withdraw. Jak reszta stanu banku —
   * CELOWO poza PlayerSave.
   */
  bankDeposits: Array<{
    id: string;
    currency: 'coins' | 'gems';
    amount: number;
    rateBps: number;
    termDays: number;
    maturesAt: string;
    createdAt: string;
  }> | null;

  // ─── Bieżący gameplay state ──────────────────────────────────────────────
  coins: Coins;
  /**
   * Kryształy (gems) — druga waluta dla Trading Plaza (Etap 5, sub-step
   * 5.0.A). Drop z kryształowych skrzynek (server-authoritative przez
   * /world/tick + dest-crystal w `destructibles.config.ts`), plus daily
   * reward, merge bonus, sprzedaż peta do NPC.
   *
   * Wartość hydratowana z `save.currencies.gems` w bootstrap (analogicznie
   * do `coins`). HUD pokazuje 3. ikonę (`ui_gem.png`) obok coins/power.
   */
  gems: number;
  /**
   * Generyczny portfel walut eventowych (id → ilość, np. `event_shells`).
   * Reflektor `save.eventCurrencies`. Hydratowane w bootstrap + po każdej akcji
   * (przez `syncStoresFromSave`). HUD pokazuje plakietkę waluty AKTYWNEGO eventu
   * (czyta nazwę/ikonę z `events.config`, saldo stąd).
   */
  eventCurrencies: Record<string, number>;
  /**
   * Stan eventów per eventId (reflektor `save.events`). EV-3: album kolekcjonerski
   * `events[eventId].album = { discovered, claimed }`. Hydratowane w bootstrap +
   * po akcji (`syncStoresFromSave`). Album modal czyta odkryte/claimed stąd
   * (reaktywne — po claimie/hatchu odświeża się automatycznie).
   */
  events: Record<string, {
    optIn?: boolean;
    progress?: number;
    album?: { discovered: string[]; claimed: boolean };
    dailyTrader?: {
      lastClaimDay?: string;
      lastClaimAt?: number;
      streakDays?: number;
      totalClaimed?: number;
    };
    pierFishing?: {
      lastClaimAt?: number;
      totalClaimed?: number;
    };
  }>;
  power: number;
  /** Bonus złota z petów gold/rainbow jako procent (50 = +50%). HUD pokazuje gdy > 0. */
  goldRainbowCoinBonus: number;

  /** Lokalna preferencja audio dla tego urzadzenia. Persistowana w localStorage. */
  musicEnabled: boolean;
  musicTrackId: MusicTrackId;

  /**
   * Plecak mikstur (defId → liczba sztuk). Reflektor `save.potions`. Hydratowane
   * w bootstrap + po każdej akcji /potions/* (przez syncStoresFromSave).
   */
  potions: PotionsBag;
  /**
   * Aktywne buffy czasowe (stat → { mult, expiresAt }). Reflektor
   * `save.activeEffects`. ActiveBuffsHud czyta to i odlicza czas.
   */
  activeEffects: ActiveEffects;

  // ─── First-run flow + customization ──────────────────────────────────────
  /**
   * `isFirstRun` (legacy) — DEPRECATED, używaj `avatarSelected/starterPetsChosen`.
   * Pozostawiony żeby istniejący code (np. dev helpers) nie crashował.
   * Wartość: `!avatarSelected` (pierwszy ekran onboardingu).
   */
  isFirstRun: boolean;
  avatarSkinId: AvatarSkinId;
  /**
   * Odblokowane skiny awatara (`profile.unlockedSkins`). Reflektor save'a —
   * hydratowany w bootstrap + po akcji (claim albumu dorzuca letnie skiny).
   * Selektor „Wygląd" (`AvatarCustomizeModal`) pokazuje skiny z tej listy.
   */
  unlockedSkins: string[];

  // ─── Onboarding (sesja 30) ───────────────────────────────────────────────
  /**
   * Onboarding 3-fazowy. Każda flaga = jeden krok ukończony.
   *
   * `avatarSelected`     — gracz wybrał skin (AvatarSelectorModal zamknięty)
   * `starterPetsChosen`  — gracz wybrał 2 startery z 4 (StarterPetSelectorModal)
   * `hasSeenFabTutorial` — gracz zobaczył tutorial strzałkę FAB (lub po timeout)
   *
   * Phaser w `main.tsx` czeka aż `avatarSelected && starterPetsChosen` (oba true)
   * przez `waitForOnboardingCompletion()`. WorldScene czyta `save.equipped` przy
   * starcie — bez wybranych petów byłby pusty świat (sesja 30 review).
   *
   * Hydratacja z `save.flags.*` w `main.tsx`. Setters też zapisują z powrotem
   * do save'a (modal woła `setAvatarSelected(true)` po wyborze + persistuje).
   */
  avatarSelected: boolean;
  starterPetsChosen: boolean;
  hasSeenFabTutorial: boolean;

  // ─── Inventories ─────────────────────────────────────────────────────────
  /** Niewyklute jajka. Hydratowane z save.eggs. */
  eggInventory: EggInstance[];
  /** Wyklute pety. Hydratowane z save.inventory. */
  petInventory: PetInstance[];
  /** UIDs equipped petów (max equippedCap(upgrades.pet_slots)). Reflektor save.equipped. */
  equippedUids: string[];
  /**
   * Poziomy upgrade'ów. Reflektor `save.upgrades` (Record<UpgradeId, number>).
   * Sesja 24: dodane żeby React komponenty reagowały na zakup. Recipe nadal
   * czyta `current.upgrades` w środku `update()` (defense in depth), ale
   * presenter layer subskrybuje TĘ wartość — po purchase UI automatycznie
   * pokazuje nowy level / cap.
   */
  upgrades: Record<string, number>;

  /**
   * Aktualny biome id (sesja 26 — dodanie biomu Mountain). Reflektor
   * `save.currentBiomeId`. HUD wyświetla na podstawie tego pola nazwę biomu.
   * Dev switch zmienia save + setter tutaj + emituje 'biome:changed' do Phasera.
   */
  currentBiomeId: string;

  // ─── Modale & overlay ────────────────────────────────────────────────────
  /** Aktualnie otwarty modal HUD-level. */
  openModal: OpenModal;
  /**
   * Kontekst dla BrowseStallModal (5.B.5b-2): gdy ustawiony, modal pokazuje
   * oferty TYLKO tej budki (klik cudzej budki na placu). `null` = przegląd
   * wszystkich ofert instancji (otwarcie bez kontekstu).
   */
  browseStallId: string | null;
  /**
   * Preselekcja listingu w BrowseStallModal (5.B.6c): gdy gracz kliknie peta
   * chodzącego w paddocku, modal otwiera się tak jak normalnie + auto-scrolluje
   * do tego konkretnego peta + delikatny highlight przez ~1.2 s. `null` =
   * brak preselekcji (klik budki / FAB → modal pokazuje wszystkie listingi
   * stalla bez highlightu).
   */
  preselectedListingPetUid: string | null;
  /**
   * Z którą zakładką otworzyć przegląd budki (E-3). Ustawiane przy wejściu
   * przez KOSZYK z jajkami — bez tego dotknięcie koszyka otwierało zakładkę
   * „Pety" i dziecko musiało szukać jajek, które przed chwilą widziało.
   */
  browseStallTab: 'pets' | 'eggs' | null;
  /** Stan handlu bezpośredniego (5.C.1) — null gdy brak. */
  trade: TradeUiState | null;
  /** Krótki komunikat handlu (odrzucono/anulowano/błąd) — toast. null = brak. */
  tradeNotice: string | null;
  stallSaleNotices: StallSale[];
  seenStallSaleIds: string[];
  tradeHistoryReturnModal: 'myStall' | 'account' | null;
  myStallDraft: { pickUid: string; price: string; tab: 'pets' | 'eggs' };
  /** Moja oferta w handlu (5.C.2) — pety + 💎. */
  tradeMyOffer: TradeOfferView;
  /** Oferta drugiej strony (5.C.2). */
  tradeTheirOffer: TradeOfferView;
  /** Kolejka toastów odblokowanych achievementów (5.D.2d). */
  achievementToasts: AchievementToastItem[];
  /**
   * Nagrody „Pełny album" gotowe do odebrania (id osiągnięć) — czerwona kropka
   * na „Osiągn." w menu FAB. Źródła: GET /achievements/me (bootstrap + otwarcie
   * modala) i komplet biomu wykryty w albumie Banku; odbiór usuwa id.
   */
  claimableAchievementIds: string[];
  /** Aktywny wynik wyklucia — null = brak. Gdy ustawiony, HatchAnimationModal się pokazuje. */
  hatchResult: HatchResult | null;
  /**
   * Aktywny wynik bulk hatcha (5.D.3c). Niezależny od `hatchResult` — single
   * i bulk flow nie mogą wystąpić jednocześnie (UI gateuje), ale trzymane
   * osobno żeby finalny panel wiedział którą wersję renderować.
   */
  hatchBulkResult: HatchBulkResult | null;
  /**
   * Faza cinematic hatcha (sesja 29 — Step A). `null` = brak hatcha, `animating`
   * = Phaser HatchScene aktywna (React UI ukryte), `showResult` = panel z petem
   * widoczny. Patrz typ `HatchPhase` powyżej.
   */
  hatchPhase: HatchPhase;
  /**
   * Wskazówka dla `MergePanel` którą grupę preselectować po otwarciu.
   * Format: `${defId}::${variant}` (variant ∈ {normal, gold}).
   * Ustawione przez PetDetailPanel klikiem w "Merge X/Y". MergePanel konsumuje
   * przy mount (kopia do lokalnego `selectedKey`) i czyści, żeby kolejne
   * otwarcie z głównego menu nie miało zaszłości.
   */
  mergePreselectKey: string | null;

  /**
   * Merge UX rework (`DESIGN_MERGE_UX_REWORK.md`, D2 decision): po sukcesie
   * mergea MergePanel ustawia uid nowo-utworzonego peta + przełącza modal na
   * `'pets'`. `PetInventoryModal` na mount konsumuje preselect → otwiera
   * `PetDetailPanel` dla tego peta. Dziecko widzi „co dostało" bez szukania.
   * Konsumowane on-mount (jak `mergePreselectKey`).
   */
  petDetailPreselectUid: string | null;

  // ─── Settery ─────────────────────────────────────────────────────────────
  // Sesja 33 — auth setters
  setAuth: (isAuthenticated: boolean, user: AuthUserSummary | null) => void;
  // Sesja 34B — save load phase
  setSaveLoaded: (v: boolean) => void;
  // Asset preload phase — PreloadScene raportuje postęp ładowania grafik
  setAssetLoadProgress: (p: number) => void;
  setAssetsLoaded: (v: boolean) => void;
  // Etap 5, 5.0.0b — Trading Plaza config fetched at bootstrap
  setTradingConfig: (config: TradingPlazaConfig, version: string) => void;
  // BANK-0 — Bank config fetched at bootstrap (wzorzec jak wyżej)
  setBankConfig: (config: BankConfig, version: string) => void;
  /** BANK-A2 — pełny album z `GET /bank/state` (null = wyczyść/przeładuj). */
  setBankAlbum: (album: Record<string, BankStickerState> | null) => void;
  setBankVaultCount: (count: number) => void;
  setBankVaultCap: (cap: number) => void;
  setEconomyResyncRequired: (v: boolean) => void;
  /** BANK-A2 — punktowa aktualizacja jednej naklejki/fotografii z serwera. */
  mergeBankSticker: (defId: string, entry: BankStickerState) => void;
  /** BANK-B — prace z `GET /bank/state` / po akcjach (null = wyczyść). */
  setBankJobs: (jobs: GameState['bankJobs']) => void;
  /** BANK-C — lokaty z `GET /bank/state` / po akcjach (null = wyczyść). */
  setBankDeposits: (deposits: GameState['bankDeposits']) => void;
  setCoins: (amount: Coins) => void;
  // Etap 5, 5.0.A — kryształy w HUD
  setGems: (amount: number) => void;
  /** Eventy (EV-1a) — ustawia portfel walut eventowych (z save / delty). */
  setEventCurrencies: (currencies: Record<string, number>) => void;
  setEvents: (events: GameState['events']) => void;
  setPower: (amount: number) => void;
  setGoldRainbowCoinBonus: (pct: number) => void;
  setMusicEnabled: (enabled: boolean) => void;
  setMusicTrackId: (trackId: MusicTrackId) => void;
  /** Mikstury — ustawia plecak (z save / delty). */
  setPotions: (potions: PotionsBag) => void;
  /** Mikstury — ustawia aktywne buffy (z save / delty). */
  setActiveEffects: (effects: ActiveEffects) => void;
  setIsFirstRun: (v: boolean) => void;
  setAvatarSkinId: (id: AvatarSkinId) => void;
  setUnlockedSkins: (skins: string[]) => void;
  // Sesja 30 — onboarding settery
  setAvatarSelected: (v: boolean) => void;
  setStarterPetsChosen: (v: boolean) => void;
  setHasSeenFabTutorial: (v: boolean) => void;
  setEggInventory: (eggs: EggInstance[]) => void;
  setPetInventory: (pets: PetInstance[]) => void;
  setEquippedUids: (uids: string[]) => void;
  setUpgrades: (upgrades: Record<string, number>) => void;
  setCurrentBiomeId: (id: string) => void;
  setOpenModal: (modal: OpenModal) => void;
  /** Ustawia kontekst budki dla BrowseStallModal (null = wszystkie oferty). */
  setBrowseStallId: (stallId: string | null) => void;
  setPreselectedListingPetUid: (petUid: string | null) => void;
  /** Z którą zakładką otworzyć przegląd budki (E-3). Koszyk z jajkami → 'eggs'. */
  setBrowseStallTab: (tab: 'pets' | 'eggs' | null) => void;
  /** Ustawia stan handlu bezpośredniego (5.C.1). null → resetuje też oferty. */
  setTrade: (trade: TradeUiState | null) => void;
  /** Ustawia toast handlu (null = ukryj). */
  setTradeNotice: (notice: string | null) => void;
  pushStallSales: (sales: StallSale[]) => void;
  dismissStallSale: (id: string) => void;
  openTradeHistory: (returnModal: 'myStall' | 'account' | null) => void;
  setMyStallDraft: (draft: { pickUid: string; price: string; tab: 'pets' | 'eggs' }) => void;
  /** Ustawia oferty handlu (5.C.2) — z `trade_state`. */
  setTradeOffers: (mine: TradeOfferView, theirs: TradeOfferView) => void;
  /** Dodaje toasty achievementów (5.D.2d) — z odpowiedzi akcji (buy/list/trade). */
  pushAchievementUnlocks: (unlocks: Array<{ id: string; label: string; crystals_reward: number }>) => void;
  /** Usuwa toast achievementu po `key` (auto-dismiss / klik). */
  dismissAchievementToast: (key: number) => void;
  /** Zastępuje listę nagród do odebrania (pełna wiedza z GET /achievements/me). */
  setClaimableAchievementIds: (ids: string[]) => void;
  /** Dopisuje nagrodę do odebrania (komplet biomu w albumie), bez duplikatów. */
  addClaimableAchievementId: (id: string) => void;
  /** Usuwa nagrodę z listy (po odbiorze). */
  removeClaimableAchievementId: (id: string) => void;
  setHatchResult: (result: HatchResult | null) => void;
  /** 5.D.3c — bulk hatch result setter. Null = clear. */
  setHatchBulkResult: (result: HatchBulkResult | null) => void;
  setHatchPhase: (phase: HatchPhase) => void;
  setMergePreselectKey: (key: string | null) => void;
  /** Merge UX rework D2: preselect dla `PetDetailPanel` po sukcesie mergea. */
  setPetDetailPreselectUid: (uid: string | null) => void;
  setBossIntroActive: (active: boolean) => void;
  setActiveScene: (scene: 'world' | 'plaza' | 'sanctum' | 'halloweenBoss') => void;
  /** Włącza/wyłącza tryb teleportu (ukrycie UI na czas portalu). */
  setTeleporting: (v: boolean) => void;
}

export const useGameStore = create<GameState>((set) => ({
  // Sesja 33 — auth init false (main.tsx hydratuje przez probe /auth/me)
  isAuthenticated: false,
  currentUser: null,
  bossIntroActive: false,
  activeScene: 'world',
  isTeleporting: false,
  // Sesja 34B — save load false do czasu hydratacji z serwera
  saveLoaded: false,
  // Asset preload — false do czasu aż PreloadScene skończy (load 'complete')
  assetsLoaded: false,
  assetLoadProgress: 0,
  // Etap 5, 5.0.0b — config null do czasu bootstrap fetch
  tradingConfig: null,
  tradingConfigVersion: null,
  // BANK-0 — config null do czasu bootstrap fetch
  bankConfig: null,
  bankConfigVersion: null,
  // BANK-A2 — album null do czasu pierwszego otwarcia (fetch on-mount)
  bankAlbum: null,
  bankVaultCount: 0,
  bankVaultCap: 0,
  economyResyncRequired: false,
  // BANK-B — prace null do czasu pierwszego otwarcia
  bankJobs: null,
  // BANK-C — lokaty null do czasu pierwszego otwarcia
  bankDeposits: null,
  coins: String(STARTING_CURRENCIES.coins),
  // Etap 5, 5.0.A — gems init z STARTING_CURRENCIES (0). Hydratowane z
  // save.currencies.gems w bootstrap (sesja 34B pattern jak coins).
  gems: STARTING_CURRENCIES.gems,
  // Eventy (EV-1a) — pusty portfel; hydratowany z save.eventCurrencies.
  eventCurrencies: {},
  events: {},
  power: 0,
  goldRainbowCoinBonus: 0,
  musicEnabled: readMusicEnabledPreference(),
  musicTrackId: readMusicTrackPreference(),
  potions: {},
  activeEffects: {},
  isFirstRun: false,
  avatarSkinId: DEFAULT_STARTER_SKIN_ID,
  unlockedSkins: [DEFAULT_STARTER_SKIN_ID],
  // Sesja 30 — onboarding init false (main.tsx hydratuje z save.flags)
  avatarSelected: false,
  starterPetsChosen: false,
  hasSeenFabTutorial: false,
  eggInventory: [],
  petInventory: [],
  equippedUids: [],
  upgrades: {},
  currentBiomeId: 'biome-meadow',
  openModal: null,
  browseStallId: null,
  preselectedListingPetUid: null,
  browseStallTab: null,
  trade: null,
  tradeNotice: null,
  stallSaleNotices: [],
  seenStallSaleIds: [],
  tradeHistoryReturnModal: null,
  myStallDraft: { pickUid: '', price: '', tab: 'pets' },
  tradeMyOffer: EMPTY_TRADE_OFFER,
  tradeTheirOffer: EMPTY_TRADE_OFFER,
  achievementToasts: [],
  claimableAchievementIds: [],
  hatchResult: null,
  hatchBulkResult: null,
  hatchPhase: null,
  mergePreselectKey: null,
  petDetailPreselectUid: null,

  setAuth: (isAuthenticated, currentUser) => set((state) => ({ isAuthenticated, currentUser,
    ...(state.currentUser?.id !== currentUser?.id ? { stallSaleNotices: [], seenStallSaleIds: [],
      myStallDraft: { pickUid: '', price: '', tab: 'pets' as const }, tradeHistoryReturnModal: null } : {}),
  })),
  setSaveLoaded: (saveLoaded) => set({ saveLoaded }),
  setAssetLoadProgress: (assetLoadProgress) => set({ assetLoadProgress }),
  setAssetsLoaded: (assetsLoaded) => set({ assetsLoaded }),
  setTradingConfig: (tradingConfig, tradingConfigVersion) =>
    set({ tradingConfig, tradingConfigVersion }),
  setBankConfig: (bankConfig, bankConfigVersion) =>
    set({ bankConfig, bankConfigVersion }),
  setBankAlbum: (bankAlbum) => set({ bankAlbum }),
  setBankVaultCount: (bankVaultCount) => set({ bankVaultCount }),
  setBankVaultCap: (bankVaultCap) => set({ bankVaultCap }),
  setEconomyResyncRequired: (economyResyncRequired) => set({ economyResyncRequired }),
  mergeBankSticker: (defId, entry) =>
    set((state) => ({ bankAlbum: { ...(state.bankAlbum ?? {}), [defId]: entry } })),
  setBankJobs: (bankJobs) => set({ bankJobs }),
  setBankDeposits: (bankDeposits) => set({ bankDeposits }),
  setCoins: (coins) => set({ coins }),
  setGems: (gems) => set({ gems }),
  setEventCurrencies: (eventCurrencies) => set({ eventCurrencies }),
  setEvents: (events) => set({ events }),
  setPower: (power) => set({ power }),
  setGoldRainbowCoinBonus: (goldRainbowCoinBonus) => set({ goldRainbowCoinBonus }),
  setMusicEnabled: (musicEnabled) => set({ musicEnabled }),
  setMusicTrackId: (musicTrackId) => set({ musicTrackId }),
  setPotions: (potions) => set({ potions }),
  setActiveEffects: (activeEffects) => set({ activeEffects }),
  setIsFirstRun: (isFirstRun) => set({ isFirstRun }),
  // Sesja 30 POST review LOW (Codex D): `isFirstRun` jest legacy alias dla
  // `!avatarSelected`. Setter `setAvatarSelected` MUSI aktualizować oba żeby
  // alias pozostał spójny (dev helpers, console.log statements które jeszcze
  // czytają `isFirstRun`).
  setAvatarSelected: (avatarSelected) => set({ avatarSelected, isFirstRun: !avatarSelected }),
  setStarterPetsChosen: (starterPetsChosen) => set({ starterPetsChosen }),
  setHasSeenFabTutorial: (hasSeenFabTutorial) => set({ hasSeenFabTutorial }),
  setAvatarSkinId: (avatarSkinId) => set({ avatarSkinId }),
  setUnlockedSkins: (unlockedSkins) => set({ unlockedSkins }),
  setEggInventory: (eggInventory) => set({ eggInventory }),
  setPetInventory: (petInventory) => set({ petInventory }),
  setEquippedUids: (equippedUids) => set({ equippedUids }),
  setUpgrades: (upgrades) => set({ upgrades }),
  setCurrentBiomeId: (currentBiomeId) => set({ currentBiomeId }),
  setOpenModal: (openModal) => set({ openModal }),
  setBrowseStallId: (browseStallId) => set({ browseStallId }),
  setPreselectedListingPetUid: (preselectedListingPetUid) => set({ preselectedListingPetUid }),
  setBrowseStallTab: (browseStallTab) => set({ browseStallTab }),
  setTrade: (trade) =>
    set(
      trade === null
        ? { trade: null, tradeMyOffer: EMPTY_TRADE_OFFER, tradeTheirOffer: EMPTY_TRADE_OFFER }
        : { trade },
    ),
  setTradeNotice: (tradeNotice) => set({ tradeNotice }),
  pushStallSales: (sales) => set((state) => {
    const userId = state.currentUser?.id;
    const persisted = userId ? readSeenSaleIds(userId) : [];
    const merged = mergeSaleNotices(state.stallSaleNotices, [...persisted, ...state.seenStallSaleIds], sales);
    // Persist at receipt, including WS events; a reload before the next HTTP poll must not replay them.
    if (userId) writeSeenSaleIds(userId, merged.seen);
    return { stallSaleNotices: merged.queue, seenStallSaleIds: merged.seen };
  }),
  dismissStallSale: (id) => set((state) => ({ stallSaleNotices: state.stallSaleNotices.filter((sale) => sale.id !== id) })),
  openTradeHistory: (tradeHistoryReturnModal) => set({ openModal: 'tradeHistory', tradeHistoryReturnModal }),
  setMyStallDraft: (myStallDraft) => set({ myStallDraft }),
  setTradeOffers: (tradeMyOffer, tradeTheirOffer) => set({ tradeMyOffer, tradeTheirOffer }),
  pushAchievementUnlocks: (unlocks) =>
    set((state) => ({
      achievementToasts: [
        ...state.achievementToasts,
        ...unlocks.map((u) => ({
          key: ++achievementToastSeq,
          id: u.id,
          label: u.label,
          crystalsReward: u.crystals_reward,
        })),
      ],
    })),
  dismissAchievementToast: (key) =>
    set((state) => ({ achievementToasts: state.achievementToasts.filter((t) => t.key !== key) })),
  setClaimableAchievementIds: (claimableAchievementIds) => set({ claimableAchievementIds }),
  addClaimableAchievementId: (id) =>
    set((state) =>
      state.claimableAchievementIds.includes(id)
        ? state
        : { claimableAchievementIds: [...state.claimableAchievementIds, id] },
    ),
  removeClaimableAchievementId: (id) =>
    set((state) => ({
      claimableAchievementIds: state.claimableAchievementIds.filter((x) => x !== id),
    })),
  setBossIntroActive: (bossIntroActive) => set({ bossIntroActive }),
  setActiveScene: (activeScene) => set({ activeScene }),
  setTeleporting: (isTeleporting) => set({ isTeleporting }),
  setHatchResult: (hatchResult) => set({ hatchResult }),
  setHatchBulkResult: (hatchBulkResult) => set({ hatchBulkResult }),
  setHatchPhase: (hatchPhase) => set({ hatchPhase }),
  setMergePreselectKey: (mergePreselectKey) => set({ mergePreselectKey }),
  setPetDetailPreselectUid: (petDetailPreselectUid) => set({ petDetailPreselectUid }),
}));
